/* eslint-disable react/jsx-no-bind */
import { Link } from "react-router-dom";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { applySearchParams } from "~/components/build/Show/lib/urlForView";
import {
  SubNavBar,
  SubNavBarRow,
  dockedSubNavLinkClassName,
} from "~/components/build/Show/components/SubNavLink";
import Count from "~/components/build/Show/components/Count";
import BuildAnnotations from "~/components/build/Show/components/annotations/BuildAnnotations";
import JobAnnotations from "~/components/build/Show/components/annotations/JobAnnotations";
import { AnnotationScope } from "~/components/build/Show/lib/types";
import { getAnnotationCounts } from "~/components/build/Show/lib/annotationCounts";

const TABS: AnnotationScope[] = ["build", "job"];

const TAB_LABELS: Record<AnnotationScope, string> = {
  build: "Build",
  job: "Jobs",
};

// The URL serializes "job" as "jobs" for backward compatibility with bookmarked
// links. The internal model uses AnnotationScope ("build" | "job") to match the
// API and the underlying enum.
const TAB_URL_VALUES: Record<AnnotationScope, string> = {
  build: "build",
  job: "jobs",
};

const DEFAULT_TAB: AnnotationScope = "build";

function tabFromUrlValue(value: string | null | undefined): AnnotationScope | null {
  const entry = TABS.find((tab) => TAB_URL_VALUES[tab] === value);
  return entry ?? null;
}

function AnnotationsTabLink({
  tab,
  isActive,
  count,
}: {
  tab: AnnotationScope;
  isActive: boolean;
  count?: number;
}) {
  const params = useQueryParams();
  const to = applySearchParams("", {
    ...params,
    annotations_tab: tab === DEFAULT_TAB ? undefined : TAB_URL_VALUES[tab],
  });

  if (Features.BuildPageListView) {
    return (
      <Link to={to} className={dockedSubNavLinkClassName({ isActive })}>
        {TAB_LABELS[tab]}
        {count != null && count > 0 && (
          <span className="ml-1">
            <Count>{count}</Count>
          </span>
        )}
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className={twMerge(
        classNames(
          "self-center px-2 py-1 text-sm",
          "flex items-center justify-center rounded-md border border-gray-400",
          "text-charcoal-400 no-underline hover:text-current hover:no-underline focus:text-current focus:no-underline",
          "transition-colors duration-75 hover:border-slate-400 hover:bg-white",
          {
            "text-charcoal-700 shadow-xs border-slate-500 bg-white": isActive,
          },
        ),
      )}
    >
      {TAB_LABELS[tab]}
      {count != null && count > 0 && (
        <span className="ml-1">
          <Count>{count}</Count>
        </span>
      )}
    </Link>
  );
}

function AnnotationsContent({ activeTab }: { activeTab: AnnotationScope }) {
  switch (activeTab) {
    case "build":
      return <BuildAnnotations />;
    case "job":
      return <JobAnnotations />;
  }
}

export default function AnnotationsPage() {
  const { annotations_tab } = useQueryParams();
  const activeTab = tabFromUrlValue(annotations_tab) ?? DEFAULT_TAB;
  const { build } = useBuild();
  const annotationCounts = getAnnotationCounts(build);

  return (
    <div data-testid="AnnotationsPage" className="flex min-w-0 flex-auto flex-col">
      <SubNavBar>
        <SubNavBarRow>
          {Features.BuildPageListView ? (
            <div className="flex items-center gap-0.5 self-center">
              {TABS.map((tab) => (
                <AnnotationsTabLink
                  key={tab}
                  tab={tab}
                  isActive={tab === activeTab}
                  count={annotationCounts[tab]}
                />
              ))}
            </div>
          ) : (
            TABS.map((tab) => (
              <AnnotationsTabLink
                key={tab}
                tab={tab}
                isActive={tab === activeTab}
                count={annotationCounts[tab]}
              />
            ))
          )}
        </SubNavBarRow>
      </SubNavBar>

      <div className="relative flex-auto">
        <div className="absolute flex h-full w-full overflow-y-auto">
          <div className="relative flex w-full flex-1 flex-col gap-2 p-3">
            <AnnotationsContent activeTab={activeTab} />
          </div>
        </div>
      </div>
    </div>
  );
}
